import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; // Import db
import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';

const RecipeFormModal = ({ isOpen, onClose, clientUID, onSuccess }) => {
    const [recipe, setRecipe] = useState({
        name: '', description: '', ingredients: '', instructions: '', notes: '',
    });
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);

    // Reset form when modal opens or closes
    useEffect(() => {
        if (isOpen) {
            setRecipe({ name: '', description: '', ingredients: '', instructions: '', notes: '' });
            setStatus('');
        }
    }, [isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setRecipe(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!clientUID) {
            setStatus('Error: Client authentication failed.');
            return;
        }

        setLoading(true);
        setStatus('Uploading recipe...');

        try {
            // Convert ingredient string (separated by new lines) to an array
            const ingredientsArray = recipe.ingredients.split('\n')
                .map(item => item.trim())
                .filter(item => item.length > 0);
            
            const recipeData = {
                ...recipe,
                ingredients: ingredientsArray,
                clientUID: clientUID,
                uploadedAt: serverTimestamp(),
            };
            
            // ----------------------------------------------------------------------
            // STEP 1: PRIVATE WRITE (Client's folder)
            // Generate a unique ID now, so we can reuse it for the shared collection.
            // Path: recipes/[clientUID]/uploadedRecipes
            // ----------------------------------------------------------------------
            const clientRecipesRef = collection(db, 'recipes', clientUID, 'uploadedRecipes');
            const newRecipeRef = doc(clientRecipesRef); // Generates a new unique ID
            
            await setDoc(newRecipeRef, recipeData); // Write to private path

            // ----------------------------------------------------------------------
            // STEP 2: PUBLIC/SHARED WRITE (Customer visibility)
            // Write the exact same data to the 'sharedRecipes' collection, 
            // reusing the unique ID generated in Step 1.
            // Path: sharedRecipes/[recipeId]
            // ----------------------------------------------------------------------
            const sharedRecipesRef = collection(db, 'sharedRecipes');
            await setDoc(doc(sharedRecipesRef, newRecipeRef.id), recipeData);

            setStatus('✅ Recipe uploaded successfully and shared with customers!');
            
            // Wait a moment then call onSuccess (which closes the modal and re-fetches list)
            setTimeout(onSuccess, 1000); 
        } catch (error) {
            console.error("Error uploading recipe: ", error);
            setStatus(`❌ Failed to upload recipe: ${error.message}`);
        } finally {
            // Keep loading true until success closes it, or set it to false on error
            if (status.startsWith('❌') || status.startsWith('✅')) {
                setLoading(false);
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Upload New Recipe</h2>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="recipe-form-modal">
                    
                    <div className="form-group-grid">
                        <div className="form-group">
                            <label htmlFor="name">Recipe Name</label>
                            <input type="text" id="name" name="name" value={recipe.name} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                            <label htmlFor="description">Short Description</label>
                            <input type="text" id="description" name="description" value={recipe.description} onChange={handleChange} />
                        </div>
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="ingredients">Ingredients (One item per line)</label>
                        <textarea id="ingredients" name="ingredients" value={recipe.ingredients} onChange={handleChange} rows="6" placeholder="1 cup oats&#10;1 scoop protein powder&#10;1/2 cup milk" required />
                    </div>

                    <div className="form-group">
                        <label htmlFor="instructions">Instructions</label>
                        <textarea id="instructions" name="instructions" value={recipe.instructions} onChange={handleChange} rows="8" required />
                    </div>

                    <div className="form-group">
                        <label htmlFor="notes">Client Notes (Optional)</label>
                        <input type="text" id="notes" name="notes" value={recipe.notes} onChange={handleChange} />
                    </div>

                    <button type="submit" disabled={loading || !clientUID}>
                        {loading ? 'Saving...' : 'Upload Recipe'}
                    </button>
                </form>

                {status && <p className={`status-message ${status.startsWith('✅') ? 'success' : 'error'}`}>{status}</p>}
            </div>
        </div>
    );
};

export default RecipeFormModal;
